// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Geographic projection for the fleet map.
//
// The map asset (usGeometry.ts) ships already projected, so this is not a
// free choice of projection: to place a marker correctly on that artwork we
// have to reproduce the transform the artwork was drawn with.
//
// Which one that is was determined empirically rather than assumed. Fitting
// candidate projection families against all 49 state centroids gives:
//
//     Albers equal-area conic   ~0.27px mean error   <- this one
//     Mercator                 ~19.9px mean error
//     Equirectangular          ~19.4px mean error
//
// with standard parallels 29.5°N / 45.5°N and central meridian 96°W — the
// d3 `geoAlbersUsa` defaults, which is what a 960x600 US atlas SVG is
// almost always rendered with. The residual is fully explained by the
// precision of the reference centroids, and known cities land inside their
// own state's polygon (projection.test.ts asserts both).
//
// Consequence: usGeometry.ts and this file must change together.

import { GEOMETRY_BOUNDS } from './usGeometry';

/** Standard parallels of the conic, in degrees. */
const PARALLEL_1 = 29.5;
const PARALLEL_2 = 45.5;
/** Central meridian, in degrees. */
const CENTRAL_MERIDIAN = -96;

/** Similarity transform from projection units into the asset's pixel space. */
const SCALE = 1270.575;
const X_OFFSET = 490.1174;
const Y_OFFSET = -1333.5803;

const toRadians = (deg: number): number => (deg * Math.PI) / 180;

// Conic constants, derived once from the standard parallels.
const PHI_1 = toRadians(PARALLEL_1);
const PHI_2 = toRadians(PARALLEL_2);
/** Cone constant: how much of a full circle one revolution of longitude spans. */
const N = (Math.sin(PHI_1) + Math.sin(PHI_2)) / 2;
const C = Math.cos(PHI_1) ** 2 + 2 * N * Math.sin(PHI_1);

/** Latitudes are clamped to where the conic stays real-valued. */
const MAX_LAT = 89;
const MIN_LAT = -89;

export interface Point {
  x: number;
  y: number;
}

export interface LatLon {
  lat: number;
  lon: number;
}

/**
 * Project a real-world coordinate into the map asset's coordinate space.
 *
 * The result is NOT clamped to the artwork's bounds: a site outside the
 * contiguous US projects to a point outside it, and callers are expected to
 * notice rather than have it silently pinned to an edge, which would draw a
 * data center somewhere it isn't. `isWithinGeometry` is that check.
 */
export function project({ lat, lon }: LatLon): Point {
  const phi = toRadians(Math.max(MIN_LAT, Math.min(MAX_LAT, lat)));
  const theta = N * (toRadians(lon) - toRadians(CENTRAL_MERIDIAN));

  const radicand = C - 2 * N * Math.sin(phi);
  const rho = Math.sqrt(Math.max(0, radicand)) / N;

  return {
    x: SCALE * (rho * Math.sin(theta)) + X_OFFSET,
    y: SCALE * (rho * Math.cos(theta)) + Y_OFFSET,
  };
}

/**
 * Does a projected point actually fall on the map artwork?
 *
 * The asset covers the contiguous 48 states plus DC — Alaska and Hawaii are
 * not drawn. A site there projects to a valid point that lands off the
 * artwork, and callers use this to surface it explicitly instead of drawing
 * a marker in blank space.
 */
export function isWithinGeometry(point: Point): boolean {
  return (
    point.x >= GEOMETRY_BOUNDS.minX &&
    point.x <= GEOMETRY_BOUNDS.maxX &&
    point.y >= GEOMETRY_BOUNDS.minY &&
    point.y <= GEOMETRY_BOUNDS.maxY
  );
}
